import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KERALA_DISTRICTS = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha', 'Kottayam',
  'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad', 'Malappuram',
  'Kozhikode', 'Wayanad', 'Kannur', 'Kasaragod'
];

const SAMPLE_IMAGES = [
  'https://images.unsplash.com/photo-1570709073200-5c34c5fa8688',
  'https://images.unsplash.com/photo-1524227489942-c14a3dc8422c',
  'https://images.unsplash.com/photo-1643602475901-9f67ab7d8196'
];

export default function AddProperty() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: '',
    listing_type: '',
    price: '',
    district: '',
    city: '',
    bedrooms: '',
    bathrooms: '',
    area: '',
    amenities: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const propertyData = {
        title: formData.title,
        description: formData.description,
        property_type: formData.property_type,
        listing_type: formData.listing_type,
        price: parseFloat(formData.price),
        location: {
          district: formData.district,
          city: formData.city
        },
        images: SAMPLE_IMAGES,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        area: formData.area ? parseFloat(formData.area) : null,
        amenities: formData.amenities ? formData.amenities.split(',').map(a => a.trim()) : []
      };

      await axios.post(`${API}/properties`, propertyData, { withCredentials: true });
      
      toast.success('Property added successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error adding property:', error);
      toast.error(error.response?.data?.detail || 'Failed to add property');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <Button
          data-testid="back-to-dashboard"
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </Button>

        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="add-property-heading">
              Add New Property
            </h1>
            <p className="text-lg text-slate-600">List your property for sale or rent</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Property Title *</label>
              <Input
                data-testid="property-title"
                type="text"
                placeholder="e.g., Spacious 3BHK Villa in Kochi"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <Textarea
                data-testid="property-description"
                rows={4}
                placeholder="Describe your property..."
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Property Type *</label>
                <Select
                  required
                  value={formData.property_type}
                  onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                >
                  <SelectTrigger data-testid="property-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="land">Land</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Listing Type *</label>
                <Select
                  required
                  value={formData.listing_type}
                  onValueChange={(value) => setFormData({ ...formData, listing_type: value })}
                >
                  <SelectTrigger data-testid="listing-type-select">
                    <SelectValue placeholder="Select listing" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">For Sale</SelectItem>
                    <SelectItem value="rent">For Rent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Price (₹) *</label>
              <Input
                data-testid="property-price"
                type="number"
                placeholder="e.g., 5000000"
                required
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">District *</label>
                <Select
                  required
                  value={formData.district}
                  onValueChange={(value) => setFormData({ ...formData, district: value })}
                >
                  <SelectTrigger data-testid="district-select">
                    <SelectValue placeholder="Select district" />
                  </SelectTrigger>
                  <SelectContent>
                    {KERALA_DISTRICTS.map((district) => (
                      <SelectItem key={district} value={district}>
                        {district}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">City</label>
                <Input
                  data-testid="property-city"
                  type="text"
                  placeholder="e.g., Kochi"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Bedrooms</label>
                <Input
                  data-testid="property-bedrooms"
                  type="number"
                  placeholder="e.g., 3"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Bathrooms</label>
                <Input
                  data-testid="property-bathrooms"
                  type="number"
                  placeholder="e.g., 2"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Area (sq.ft)</label>
                <Input
                  data-testid="property-area"
                  type="number"
                  placeholder="e.g., 1500"
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Amenities (comma separated)</label>
              <Input
                data-testid="property-amenities"
                type="text"
                placeholder="e.g., Parking, Swimming Pool, Gym"
                value={formData.amenities}
                onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
              />
            </div>

            <div className="flex gap-4 pt-6">
              <Button
                data-testid="submit-property"
                type="submit"
                className="flex-1 bg-primary hover:bg-primary/90 text-white h-12"
                disabled={loading}
              >
                {loading ? 'Adding Property...' : 'Add Property'}
              </Button>
              <Button
                data-testid="cancel-add-property"
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="h-12"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}